import React, { useEffect, useMemo, useState } from 'react'
import { VehicleInspection, Vehicle, UserRoles, PartialVehicleInspection } from '../../types'
import {
    deleteInspection,
    getInspections,
    getVehicleInspectionPDF,
    getVehicles,
} from '../../api/dataService'
import { Navigate } from 'react-router-dom'
import StackedContentContainer from '../ContentContainer'
import { ColumnDef } from '@tanstack/react-table'
import SearchableTable from '../SearchableTable'
import { checkIsAllowed, getDateString, isLoadingSpinner } from '../../util/Helpers'
import { Alert, Button, ButtonGroup, Form, Modal } from 'react-bootstrap'
import DeleteConfirmationModal from '../DeleteConfirmationModal'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/reduxTypes'
import CreateInspectionEntry from '../CreateInspectionEntry'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays } from 'date-fns'

const ReadOnlyDateInput = React.forwardRef<HTMLInputElement, React.HTMLProps<HTMLInputElement>>(
    (props, ref) => <input {...props} readOnly ref={ref} />
);

const parentStyle = { padding: '2rem' }

const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

type ViewMode = 'day' | 'week' | 'month' | 'all'

function ManageInspectionsPage() {
    const user = useSelector((state: RootState) => state.user)

    const canCreate = checkIsAllowed(user.groups, [
        UserRoles.ownership,
        UserRoles.management,
        UserRoles.officeAdmin,
        UserRoles.supervisor,
        UserRoles.dispatch,
        UserRoles.driver,
    ])
    const canEdit = checkIsAllowed(user.groups, [
        UserRoles.ownership,
        UserRoles.management,
        UserRoles.officeAdmin,
    ])

    const [inspectionList, setInspectionList] = useState<VehicleInspection[]>([])
    const [vehicleList, setVehicleList] = useState<Vehicle[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [redirectPath, setRedirectPath] = useState<string | null>(null)

    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedToUpdate, setSelectedToUpdate] = useState<PartialVehicleInspection | null>(null)
    const [selectedToDelete, setSelectedToDelete] = useState<VehicleInspection | null>(null)
    const [successBanner, setSuccessBanner] = useState<string | null>(null)

    const [viewMode, setViewMode] = useState<ViewMode>('day')
    const [selectedDate, setSelectedDate] = useState<Date>(() => toMidnight(new Date()))
    const [selectedVehicleId, setSelectedVehicleId] = useState<string | 'all'>('all')

    useEffect(() => {
        if (!successBanner) return
        const timer = setTimeout(() => setSuccessBanner(null), 6000)
        return () => clearTimeout(timer)
    }, [successBanner])

    useEffect(() => {
        getVehicles()
            .then(res => {
                if ('redirectTo' in res) { setRedirectPath(res.redirectTo); return }
                setVehicleList(Array.isArray(res) ? res : [])
            })
            .catch(err => console.error('Error fetching vehicles:', err))
    }, [])

    useEffect(() => {
        setIsLoading(true)
        getInspections()
            .then(res => {
                if ('redirectTo' in res) { setRedirectPath((res as any).redirectTo); return }
                const all = Array.isArray(res) ? res : []
                setInspectionList(all.filter(i => i.status !== 'deleted'))
            })
            .catch(err => console.error('Error fetching inspections:', err))
            .finally(() => setIsLoading(false))
    }, [])

    const range = useMemo(() => {
        if (viewMode === 'all') return null
        if (viewMode === 'day') {
            const start = toMidnight(selectedDate)
            return { start, end: addDays(start, 1) }
        }
        if (viewMode === 'week') {
            const start = toMidnight(startOfWeek(selectedDate, { weekStartsOn: 1 }))
            const end = addDays(toMidnight(endOfWeek(selectedDate, { weekStartsOn: 1 })), 1)
            return { start, end }
        }
        const start = toMidnight(startOfMonth(selectedDate))
        return { start, end: addDays(toMidnight(endOfMonth(selectedDate)), 1) }
    }, [selectedDate, viewMode])

    const visibleInspections = useMemo(() => {
        const byDateAsc = (a: VehicleInspection, b: VehicleInspection) => {
            const ta = new Date(a.inspection_date).getTime()
            const tb = new Date(b.inspection_date).getTime()
            return ta - tb
        }

        let list = selectedVehicleId !== 'all'
            ? inspectionList.filter(i => String(i.vehicle?.id) === selectedVehicleId)
            : inspectionList

        if (viewMode === 'all') return [...list].sort(byDateAsc)
        if (!range) return []

        return list
            .filter(i => {
                const t = new Date(i.inspection_date).getTime()
                if (isNaN(t)) return false
                return t >= range.start.getTime() && t < range.end.getTime()
            })
            .sort(byDateAsc)
    }, [inspectionList, viewMode, range, selectedVehicleId])

    const rangeLabel = useMemo(() => {
        if (viewMode === 'day') return format(selectedDate, 'MMMM d, yyyy')
        if (viewMode === 'week') {
            const s = startOfWeek(selectedDate, { weekStartsOn: 1 })
            const e = endOfWeek(selectedDate, { weekStartsOn: 1 })
            return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`
        }
        if (viewMode === 'month') return format(selectedDate, 'MMMM yyyy')
        return 'All Time'
    }, [selectedDate, viewMode])

    const navigate = (direction: -1 | 1) => {
        setSelectedDate(prev => {
            if (viewMode === 'day') return addDays(prev, direction)
            if (viewMode === 'week') return addDays(prev, direction * 7)
            const d = new Date(prev)
            d.setMonth(d.getMonth() + direction)
            return toMidnight(d)
        })
    }

    const today = toMidnight(new Date())

    const handleCloseCreate = () => {
        setShowCreateModal(false)
        setSelectedToUpdate(null)
    }

    const handleCreate = (created: VehicleInspection) => {
        setInspectionList(prev => [created, ...prev])
        setSuccessBanner('Inspection successfully submitted.')
        handleCloseCreate()
    }

    const handleUpdate = (updated: any) => {
        setInspectionList(prev => prev.map(i => String(i.id) === String(updated.id) ? updated : i))
        setSuccessBanner('Inspection successfully updated.')
        handleCloseCreate()
    }

    const handleConfirmDelete = async () => {
        if (!selectedToDelete) return
        await deleteInspection(selectedToDelete.id.toString())
        setInspectionList(prev => prev.filter(i => String(i.id) !== String(selectedToDelete.id)))
        setSuccessBanner('Inspection deleted.')
        setShowDeleteModal(false)
        setSelectedToDelete(null)
    }

    const columns = useMemo<ColumnDef<VehicleInspection>[]>(() => [
        {
            id: 'inspection_date',
            header: 'Date / Time',
            accessorFn: row => row.inspection_date,
            cell: ({ getValue }) => {
                const v = getValue<string>()
                return v ? format(new Date(v), 'MMM d, yyyy h:mm a') : '—'
            },
        },
        {
            id: 'employee',
            header: 'Employee',
            accessorFn: row => row.employee ? `${row.employee.first_name} ${row.employee.last_name}` : '—',
        },
        {
            id: 'vehicle',
            header: 'Vehicle',
            accessorFn: row => row.vehicle ? `${row.vehicle.make} ${row.vehicle.model}` : '—',
        },
        {
            id: 'vin',
            header: 'VIN',
            accessorFn: row => row.vehicle?.vin ?? '—',
        },
        {
            id: 'odometer_start',
            header: 'Odometer Start',
            accessorFn: row => row.odometer_start ?? '—',
        },
        {
            id: 'odometer_end',
            header: 'Odometer End',
            accessorFn: row => row.odometer_end ?? '—',
        },
        {
            id: 'condition',
            header: 'Condition',
            accessorFn: row => row.pre_job_inspection_vehicle_condition_following_inspection ?? '—',
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: ({ row }) => {
                const isOwn = String(row.original.employee?.id) === String(user.userid)
                const canEditRow = canEdit || isOwn
                return (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className="btn-group-sm">
                            {canEditRow && (
                                <Button
                                    className="btn-secondary"
                                    onClick={() => {
                                        setSelectedToUpdate(row.original)
                                        setShowCreateModal(true)
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                            <Button
                                className="btn-success"
                                onClick={() => getVehicleInspectionPDF(row.original.id.toString())}
                            >
                                PDF
                            </Button>
                            {canEditRow && (
                                <Button
                                    className="btn-danger"
                                    onClick={() => {
                                        setSelectedToDelete(row.original)
                                        setShowDeleteModal(true)
                                    }}
                                >
                                    Delete
                                </Button>
                            )}
                        </ButtonGroup>
                    </div>
                )
            },
        },
    ], [canEdit, user.userid])

    if (redirectPath) return <Navigate to={redirectPath} />

    return (
        <div style={parentStyle}>
            <StackedContentContainer title={"Vehicle Inspection Directory"}>
                {successBanner && (
                    <Alert variant="success" onClose={() => setSuccessBanner(null)} dismissible>
                        {successBanner}
                    </Alert>
                )}

                {/* Header controls */}
                <div className="row align-items-center mb-3 g-2">

                    {/* Left: vehicle selector */}
                    <div className="col-12 col-md-4">
                        <Select
                            placeholder="All Vehicles"
                            isClearable
                            options={[
                                { label: 'All Vehicles', value: 'all' },
                                ...vehicleList.map(v => ({
                                    label: `${v.make} ${v.model} – ${v.vin || v.id}`,
                                    value: String(v.id),
                                })).sort((a, b) => a.label.localeCompare(b.label)),
                            ]}
                            onChange={opt => setSelectedVehicleId(opt ? opt.value : 'all')}
                        />
                    </div>

                    {/* Centre: date nav */}
                    <div className="col-12 col-md-4">
                        <div className="d-flex justify-content-center">
                            <div className="text-center" style={{ width: '100%', maxWidth: 320 }}>
                                <Form.Label className="fw-bold mb-1 d-block">Date</Form.Label>
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={viewMode === 'all'}
                                        onClick={() => navigate(-1)}
                                    >
                                        ◀
                                    </Button>
                                    <div style={{ flex: 1 }}>
                                        <DatePicker
                                            selected={selectedDate}
                                            onChange={(d: Date | null) => { if (d) setSelectedDate(toMidnight(d)) }}
                                            dateFormat="dd-MM-yy"
                                            disabled={viewMode === 'all'}
                                            customInput={<ReadOnlyDateInput className="form-control text-center" />}
                                        />
                                    </div>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={viewMode === 'all'}
                                        onClick={() => navigate(1)}
                                    >
                                        ▶
                                    </Button>
                                </div>
                                <div className="d-flex justify-content-center mt-2">
                                    <ButtonGroup size="sm">
                                        {(['day', 'week', 'month', 'all'] as ViewMode[]).map(m => (
                                            <Button
                                                key={m}
                                                variant={viewMode === m ? 'primary' : 'outline-primary'}
                                                onClick={() => setViewMode(m)}
                                            >
                                                {m.charAt(0).toUpperCase() + m.slice(1)}
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                </div>
                                <div className="mt-1">
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0"
                                        disabled={viewMode === 'all' || toMidnight(selectedDate).getTime() === today.getTime()}
                                        onClick={() => setSelectedDate(today)}
                                    >
                                        Today
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: add button */}
                    <div className="col-12 col-md-4 d-flex justify-content-md-end justify-content-start">
                        {canCreate && (
                            <Button variant="primary" onClick={() => { setSelectedToUpdate(null); setShowCreateModal(true) }}>
                                Add Inspection
                            </Button>
                        )}
                    </div>
                </div>

                {/* Table */}
                {isLoading ? isLoadingSpinner() : visibleInspections.length === 0 ? (
                    <div className="text-center py-4">
                        <h5 className="mb-2">No inspections{viewMode === 'all' ? '' : ` for ${rangeLabel}`}</h5>
                    </div>
                ) : (
                    <SearchableTable
                        columns={columns}
                        data={visibleInspections}
                        globalSearch
                    />
                )}
            </StackedContentContainer>

            {/* Create / Edit Modal */}
            <Modal show={showCreateModal} onHide={handleCloseCreate} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedToUpdate ? 'Update Inspection' : 'Add Inspection'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <CreateInspectionEntry
                        key={selectedToUpdate ? `edit-${selectedToUpdate.id}` : `create-${String(showCreateModal)}`}
                        inspectionToUpdate={selectedToUpdate ?? undefined}
                        closeModal={handleCloseCreate}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreate}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={() => { setShowDeleteModal(false); setSelectedToDelete(null) }}
                onConfirm={handleConfirmDelete}
            />
        </div>
    )
}

export default ManageInspectionsPage
